import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Play, Clock, CalendarDays } from "lucide-react";
import { formatMinutes } from "@/lib/timeFormat";
import { ClockCard, ClockInModal } from "@/components/clock/ClockCard";
import { AITimesheetTriggerButton } from "@/components/pace/AITimesheetModal";
import { usePaceStatus } from "@/hooks/use-pace-status";
import { AdminTimeEntryDialog } from "@/components/admin/AdminTimeEntryDialog";
import { useAuth } from "@/lib/auth";

/**
 * Worker clock page — top-level route wrapper. Loads the open entry,
 * tenant metadata, assigned worksites, and dimensions; delegates the
 * active-entry rendering + clock-in modal to `<ClockCard />` and
 * `<ClockInModal />`. Phase 12.2 refactor split ClockPage into four
 * files under `components/clock/`.
 */
export default function ClockPage() {
  const [openEntry, setOpenEntry] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [worksites, setWorksites] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [modal, setModal] = useState(false);
  const [durationModal, setDurationModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { paceEnabled } = usePaceStatus();
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const [cur, t, ws, dims] = await Promise.all([
        api.get("/time/current"),
        api.get("/tenant/me"),
        api.get("/time/my-worksites"),
        api.get("/tenant/dimensions?active=true"),
      ]);
      setOpenEntry(cur.data.open_entry);
      setTenant(t.data);
      setWorksites(ws.data.items);
      const withValues = await Promise.all(
        dims.data.items.map(async (d) => {
          const vr = await api.get(`/tenant/dimensions/${d.id}/values?active=true`);
          return { ...d, values: vr.data.items };
        })
      );
      setDimensions(withValues);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startClockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/time/clock-out", {});
      const mins = data?.duration_minutes;
      toast.success(
        mins != null ? `Clocked out. ${formatMinutes(mins)} recorded.` : "Clocked out"
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="clock-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">My time</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Clock</h1>
          {tenant && (
            <p className="mt-2 text-muted-foreground">
              {tenant.name} · <span className="font-mono text-xs">{tenant.timezone}</span>
            </p>
          )}
        </div>
        {paceEnabled && (
          <div data-testid="clock-page-ai-timesheet-slot">
            <AITimesheetTriggerButton onCommitted={() => load()} />
          </div>
        )}
        {tenant?.allow_worker_duration_entry && (
          <Button
            variant="outline"
            onClick={() => setDurationModal(true)}
            data-testid="worker-log-duration-btn"
            className="gap-1.5"
          >
            <CalendarDays className="h-4 w-4" /> Log past work
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : openEntry ? (
        <ClockCard
          entry={openEntry}
          tz={tenant?.timezone}
          worksite={worksites.find((w) => w.id === openEntry.worksite_id) || null}
          onClockOut={startClockOut}
          busy={busy}
        />
      ) : worksites.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Not assigned yet</CardTitle>
            <CardDescription>
              An admin needs to assign you to a worksite before you can clock in.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card className="card-elevate">
          <CardContent className="flex flex-col items-center gap-6 py-14">
            <div className="rounded-full bg-secondary/10 p-6 text-secondary">
              <Clock className="h-10 w-10" />
            </div>
            <div className="text-center">
              <div className="font-display text-2xl font-semibold text-primary">Ready when you are</div>
              <p className="mt-2 text-sm text-muted-foreground">You have no open punch. Tap below to clock in.</p>
            </div>
            <Button
              size="lg"
              className="gap-2 bg-secondary text-white hover:bg-secondary/90"
              onClick={() => setModal(true)}
              data-testid="clock-in-open-modal"
            >
              <Play className="h-5 w-5" /> Clock in
            </Button>
          </CardContent>
        </Card>
      )}

      {error && !loading && (
        <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
      )}

      <ClockInModal
        open={modal}
        onClose={() => setModal(false)}
        worksites={worksites}
        dimensions={dimensions}
        onDone={() => { setModal(false); load(); }}
      />

      {tenant?.allow_worker_duration_entry && (
        <AdminTimeEntryDialog
          open={durationModal}
          onOpenChange={setDurationModal}
          endpoint="/me/time-entries"
          workers={[]}
          worksites={worksites}
          defaultEntryMode="duration"
          lockToWorkerId={user?.id || "self"}
          allowPunchMode={false}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}
